import { Easings } from '../utils/math/Easings';

/**
 * Generic declarative per-element motion for scroll-driven SVG overlays.
 *
 * Ported from Studio-Motion-Live.html: every `[data-anim]` descendant of
 * `group` animates as a PURE FUNCTION of `progress` (0..1 over its phase's
 * scroll window). No time-based state, no CSS transition → the output is
 * fully determined by the current scroll position, so fast-scroll and
 * backward-scroll are correct by construction. `reduceMotion` jumps every
 * element straight to its end state.
 *
 * Vocabulary (space-separated in `data-anim`):
 *   rise · scale · grow-x · count   (opacity fade is implicit; opt out with
 *                                     data-no-fade)
 *
 * Per-element attributes:
 *   data-at, data-dur     window within the phase progress (default 0 / 1)
 *   data-ease             Easings key (default quadOut); always lands on 1
 *   data-fade-gain        opacity = clamp(raw * gain) (default 1; counter 4)
 *   data-no-fade          opt out of opacity animation
 *   data-origin           transform-origin (default center); set once
 *   data-rise             translateY px in SVG user units, from → 0
 *   data-scale-from       scale start (default 0.92) → 1
 *   data-count-from/-to   numeric value range for `count`
 *   data-count-decimals   fraction digits (default 0)
 *   data-count-locale     Intl locale (default de-DE)
 *   data-count-prefix     text before the number
 *   data-count-suffix     text after the number
 */
export function applyDataAnim(
  group: Element,
  progress: number,
  reduceMotion: boolean,
): void {
  const animEls = group.querySelectorAll('[data-anim]');
  animEls.forEach((node) => {
    const el = node as SVGElement;
    // One-time: enable CSS transforms in SVG user space + set the origin.
    if (el.dataset['animReady'] !== '1') {
      el.style.setProperty('transform-box', 'fill-box');
      el.style.setProperty(
        'transform-origin',
        el.getAttribute('data-origin') || 'center',
      );
      el.dataset['animReady'] = '1';
    }

    const spec = (el.getAttribute('data-anim') || '').split(/\s+/);
    const at = parseFloat(el.getAttribute('data-at') || '0');
    const dur = Math.max(0.0001, parseFloat(el.getAttribute('data-dur') || '1'));
    const easeName = el.getAttribute('data-ease') || 'quadOut';
    const easeFn =
      (Easings as Record<string, (t: number) => number>)[easeName] ||
      Easings.quadOut;
    const raw = reduceMotion
      ? 1
      : Math.min(1, Math.max(0, (progress - at) / dur));
    // Land exactly on 1 — expOut & friends asymptote just below it, which
    // would otherwise leave counts a hair short of their target value.
    const e = raw >= 1 ? 1 : easeFn(raw);

    if (!el.hasAttribute('data-no-fade')) {
      const gain = parseFloat(el.getAttribute('data-fade-gain') || '1');
      el.style.opacity = String(Math.min(1, Math.max(0, raw * gain)));
    }

    const tf: string[] = [];
    if (spec.includes('rise')) {
      const rise = parseFloat(el.getAttribute('data-rise') || '28');
      tf.push(`translateY(${(1 - e) * rise}px)`);
    }
    if (spec.includes('grow-x')) {
      tf.push(`scaleX(${e})`);
    }
    if (spec.includes('scale')) {
      const from = parseFloat(el.getAttribute('data-scale-from') || '0.92');
      tf.push(`scale(${from + (1 - from) * e})`);
    }
    if (tf.length > 0) el.style.transform = tf.join(' ');

    if (spec.includes('count')) {
      const from = parseFloat(el.getAttribute('data-count-from') || '0');
      const to = parseFloat(el.getAttribute('data-count-to') || '0');
      const dec =
        parseInt(el.getAttribute('data-count-decimals') || '0', 10) || 0;
      const locale = el.getAttribute('data-count-locale') || 'de-DE';
      const prefix = el.getAttribute('data-count-prefix') || '';
      const suffix = el.getAttribute('data-count-suffix') || '';
      const v = from + (to - from) * e;
      el.textContent =
        prefix +
        v.toLocaleString(locale, {
          minimumFractionDigits: dec,
          maximumFractionDigits: dec,
        }) +
        suffix;
    }

    // Milestone stepper: ONE group whose children show the CURRENT milestone
    // (year/number/unit/context) chosen by progress. In-place text swap → no
    // stacking, no ghosting. `data-milestones` = JSON array of
    // {y,n,w,note1,note2}; children matched by class (.ms-year/.ms-num/.ms-word/
    // .ms-note1/.ms-note2). The number snaps per milestone (the non-linear jumps
    // ARE the story); the group fades in once via the opacity block above.
    if (spec.includes('milestone')) {
      let ms: Array<Record<string, string>> = [];
      try {
        ms = JSON.parse(el.getAttribute('data-milestones') || '[]');
      } catch {
        ms = [];
      }
      if (ms.length > 0) {
        const idx = reduceMotion
          ? ms.length - 1
          : Math.min(ms.length - 1, Math.max(0, Math.floor(raw * ms.length)));
        const cur = ms[idx] || {};
        const set = (sel: string, key: string): void => {
          const t = el.querySelector(sel);
          if (t) t.textContent = cur[key] || '';
        };
        set('.ms-year', 'y');
        set('.ms-num', 'n');
        set('.ms-word', 'w');
        set('.ms-note1', 'note1');
        set('.ms-note2', 'note2');
      }
    }
  });
}
